// Submissions of one form (/submissions) — the entries a published form has
// collected, reached by clicking a Reports row. Param-less like
// /form-templates/builder: the form's identity rides in location.state and a
// direct visit redirects back to /reports.
//
// This page is now just the form's identity header: the list itself lives in
// SubmissionsList, which the Inspections → Submissions tab also renders, so the
// two listings cannot drift. Everything left here is form-specific — the
// entries/lastEntry subtitle and opening a row's report, which needs the form
// name the report screen shows in its sticky header.

import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon, IconButton } from '@/components/common';
import { SubmissionSourceType, type Submission } from '@/api/endpoints/submissionsApi';
import { fmtDateTime } from './submissionColumns';
import SubmissionsList from './SubmissionsList';

interface SubmissionsRouteState {
  formId?: string;
  formName?: string;
  /** The form's TOTAL entry count, from the Reports row. */
  entries?: number | null;
  lastEntry?: string | null;
}

const SubmissionsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { formId, formName, entries, lastEntry } =
    (location.state as SubmissionsRouteState | null) ?? {};

  // The report screen is param-less too, so the row carries everything its
  // sticky header shows — it makes no second request for identity.
  const openReport = (submission: Submission) =>
    navigate('/submissions/view-report', {
      state: {
        submissionId: submission.id,
        referenceNumber: submission.referenceNumber,
        sourceRef: submission.source?.referenceNumber ?? null,
        formName,
        completeDate: submission.completeDate,
        comments: submission.comments,
      },
    });

  // Two different numbers are both correct: the subtitle counts the form's TOTAL
  // entries (carried from the Reports row) while the toolbar counts what the
  // endpoint returns for the current filter set.
  const subtitle =
    entries === null || entries === undefined
      ? undefined
      : t('submissions_subtitle', {
          count: entries,
          when: lastEntry ? fmtDateTime(lastEntry, t) : t('text_none'),
        });

  // No form in the router state → nothing to list; go back before anything
  // paints. Render-time (not an effect) so no frame of the empty table ever
  // reaches the screen, and `replace` so Back doesn't return here to bounce
  // again — same idiom as JWTProtectedRoute.
  if (!formId) return <Navigate to='/reports' replace />;

  return (
    <div className='submissions-page'>
      {/* Back button, tile and headings share one row, as the design shows. */}
      <header className='submissions-page__identity'>
        <IconButton
          className='submissions-page__back'
          icon='arrow-left'
          aria-label={t('submissions_back')}
          onClick={() => navigate('/reports')}
        />
        <span className='submissions-page__tile' aria-hidden='true'>
          <Icon name='clipboard-list' size={24} />
        </span>
        <div className='submissions-page__headings'>
          <h1 className='submissions-page__title'>{formName}</h1>
          {subtitle && <p className='submissions-page__meta'>{subtitle}</p>}
        </div>
      </header>

      {/* One form's submissions. `skip` restates the guard above at the call
          site, so the query's precondition is visible where it is made. */}
      <SubmissionsList
        type={SubmissionSourceType.Form}
        recordIds={[formId]}
        skip={!formId}
        onOpenReport={openReport}
      />
    </div>
  );
};

export default SubmissionsPage;
